import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";
import {
  TrendingUp, Plus, Search, ChevronRight, Loader2, Trash2, X,
  User, Mail, Phone, Calendar, Target, Award, AlertTriangle,
  ArrowRight, FileText, MessageSquare, Send, Bot, UserSearch, ArrowRightLeft,
  PhoneCall, MessagesSquare, Upload, CheckCircle2, Clock, Settings2,
  GripVertical, Pencil, Save, ExternalLink
} from "lucide-react";
import { AgentChat } from "../components/AgentChat";
import { CrmRoutinesPanel } from "../components/CrmRoutinesPanel";
import { InvoiceEditor } from "../components/InvoiceEditor";
import CatalogSelector from "../components/CatalogSelector";
import AIScoreBadge from "../components/entity-detail/AIScoreBadge";

export default function CrmPage() {
  const { axiosAuth } = useAuth();
  const { societeApi, currentSociete } = useSociete();
  const { t } = useTranslation();
  const api = societeApi();
  const [opps, setOpps] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ search: "", stage: "" });
  const [view, setView] = useState("kanban");
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showAgent, setShowAgent] = useState(false);
  const [showRoutines, setShowRoutines] = useState(false);
  const [showConvert, setShowConvert] = useState(null);
  const [form, setForm] = useState({ title: "", contact_name: "", contact_email: "", amount: 0, probability: 20, notes: "", source: "", expected_close: "", client_id: "" });
  const [convertForm, setConvertForm] = useState({ type: "devis", lines: [] });
  const [saving, setSaving] = useState(false);
  const [activityText, setActivityText] = useState("");
  const [activityType, setActivityType] = useState("note");
  const [stats, setStats] = useState({});

  // InvoiceEditor state for devis creation
  const [showDevisEditor, setShowDevisEditor] = useState(false);
  const [devisForm, setDevisForm] = useState({});
  const [tags, setTags] = useState([]);

  // Pipeline config
  const [stages, setStages] = useState([]);
  const [showPipelineConfig, setShowPipelineConfig] = useState(false);
  const [editStages, setEditStages] = useState([]);

  // Devis preview
  const [companyLogo, setCompanyLogo] = useState("");
  const [companySettings, setCompanySettings] = useState({});

  // Drag state
  const [draggedOpp, setDraggedOpp] = useState(null);

  const STAGE_MAP = Object.fromEntries(stages.map(s => [s.key, s]));

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, cRes, sRes, cfgRes, logoRes, settingsRes, tagsRes] = await Promise.all([
        api.get("/crm/opportunities"),
        api.get("/admin/clients").catch(() => ({ data: [] })),
        api.get("/crm/stats").catch(() => ({ data: {} })),
        api.get("/crm/pipeline-config"),
        api.get("/admin/settings/logo").catch(() => ({ data: {} })),
        api.get("/admin/settings").catch(() => ({ data: {} })),
        api.get("/builder/tags").catch(() => ({ data: { tags: [] } })),
      ]);
      setOpps(oRes.data || []);
      setClients(cRes.data || []);
      setStats(sRes.data || {});
      setStages(cfgRes.data?.stages || []);
      setCompanyLogo(logoRes.data?.company_logo || "");
      setCompanySettings(settingsRes.data || {});
      setTags(tagsRes.data?.tags || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload, currentSociete]);

  const createOpp = async () => {
    setSaving(true);
    try {
      await api.post("/crm/opportunities", form);
      setShowCreate(false);
      setForm({ title: "", contact_name: "", contact_email: "", amount: 0, probability: 20, notes: "", source: "", expected_close: "", client_id: "" });
      reload();
    } catch {}
    setSaving(false);
  };

  const updateStage = async (oppId, newStage) => {
    await api.put(`/crm/opportunities/${oppId}`, { stage: newStage });
    // Update local state immediately
    setOpps(prev => prev.map(o => o.opportunity_id === oppId ? { ...o, stage: newStage } : o));
    if (showDetail?.opportunity_id === oppId) setShowDetail(prev => prev ? { ...prev, stage: newStage } : null);
  };

  const deleteOpp = async (id) => {
    if (!window.confirm("Supprimer cette opportunite ?")) return;
    await api.delete(`/crm/opportunities/${id}`);
    setShowDetail(null);
    reload();
  };

  const addActivity = async () => {
    if (!activityText.trim() || !showDetail) return;
    await api.post(`/crm/opportunities/${showDetail.opportunity_id}/activities`, { type: activityType, content: activityText });
    setActivityText("");
    setActivityType("note");
    const r = await api.get(`/crm/opportunities/${showDetail.opportunity_id}`);
    setShowDetail(r.data);
  };

  const convertOpp = async () => {
    setSaving(true);
    try {
      await api.post(`/crm/opportunities/${showConvert}/convert`, convertForm);
      setShowConvert(null);
      setShowDetail(null);
      reload();
    } catch {}
    setSaving(false);
  };

  // Pipeline config
  const savePipelineConfig = async () => {
    setSaving(true);
    try {
      const r = await api.put("/crm/pipeline-config", { stages: editStages });
      setStages(r.data?.stages || editStages);
      setShowPipelineConfig(false);
    } catch {}
    setSaving(false);
  };

  // Drag & Drop handlers
  const handleDragStart = (e, opp) => {
    setDraggedOpp(opp);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", opp.opportunity_id);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleDrop = (e, stageKey) => {
    e.preventDefault();
    if (draggedOpp && draggedOpp.stage !== stageKey) {
      updateStage(draggedOpp.opportunity_id, stageKey);
    }
    setDraggedOpp(null);
  };

  const filtered = opps.filter(o => {
    if (filter.search && !o.title?.toLowerCase().includes(filter.search.toLowerCase()) && !o.contact_email?.toLowerCase().includes(filter.search.toLowerCase())) return false;
    if (filter.stage && o.stage !== filter.stage) return false;
    return true;
  });

  const inputCls = "bg-[#161020] border-white/10 text-white";

  return (
    <div className="p-4 md:p-8 max-w-[1600px]" data-testid="crm-page">
      <PageHeader
        icon={<Target />}
        title={t("crm.title") || "CRM"}
        description={`${stats.total || 0} opportunités · ${stats.pipeline_value?.toLocaleString("fr") || 0} € en cours · ${stats.win_rate || 0}% taux de conversion`}
        testId="crm-header"
        actions={(
          <>
            <Button onClick={() => { setShowRoutines(!showRoutines); if (showAgent) setShowAgent(false); }} variant="outline" className={`border-white/10 text-slate-300 hover:bg-white/5 ${showRoutines ? "bg-[#f7c935]/10 border-[#f7c935]/30 text-[#f7c935]" : ""}`} data-testid="crm-routines-btn">
              <Target className="w-4 h-4 mr-1" /> Routines IA CRM
            </Button>
            <Button onClick={() => { setShowAgent(!showAgent); if (showRoutines) setShowRoutines(false); }} variant="outline" className={`border-white/10 text-slate-300 hover:bg-white/5 ${showAgent ? "bg-blue-500/10 border-blue-500/30 text-blue-400" : ""}`} data-testid="crm-agent-btn">
              <Bot className="w-4 h-4 mr-1" /> Agent CRM
            </Button>
            <Button onClick={() => { setEditStages([...stages]); setShowPipelineConfig(true); }} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="pipeline-config-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Pipeline
            </Button>
            <div className="flex border border-white/10 rounded-lg overflow-hidden">
              <button onClick={() => setView("kanban")} className={`px-3 py-2 text-xs ${view === "kanban" ? "bg-[#f7c935] text-[#14101c] font-bold" : "text-slate-400 hover:bg-white/5"}`}>Kanban</button>
              <button onClick={() => setView("list")} className={`px-3 py-2 text-xs ${view === "list" ? "bg-[#f7c935] text-[#14101c] font-bold" : "text-slate-400 hover:bg-white/5"}`}>Liste</button>
            </div>
            <Button onClick={() => setShowCreate(true)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="new-opp-btn">
              <Plus className="w-4 h-4 mr-1" /> Opportunité
            </Button>
          </>
        )}
      />

      {loading && <ListSkeleton items={6} testId="crm-loading-skeleton" />}

      {/* Kanban View */}
      {!loading && view === "kanban" && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {stages.map(stage => {
            const stageOpps = opps.filter(o => o.stage === stage.key);
            const totalAmount = stageOpps.reduce((sum, o) => sum + (o.amount || 0), 0);
            return (
              <div key={stage.key}
                className={`flex-shrink-0 w-[260px] rounded-xl border transition-colors ${draggedOpp ? "border-dashed border-white/20" : "border-white/5"}`}
                style={{ background: stage.color + "08" }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.key)}
                data-testid={`kanban-col-${stage.key}`}>
                {/* Column header */}
                <div className="p-3 border-b border-white/5">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: stage.color }} />
                      <span className="text-xs font-bold">{stage.label}</span>
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-500">{stageOpps.length}</span>
                  </div>
                  {totalAmount > 0 && <div className="text-[10px] text-slate-600">{totalAmount.toLocaleString("fr")} EUR</div>}
                </div>
                {/* Cards */}
                <div className="p-2 space-y-2 min-h-[80px]">
                  {stageOpps.map(o => (
                    <div key={o.opportunity_id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, o)}
                      onClick={() => setShowDetail(o)}
                      className="bg-[#14101c] border border-white/5 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-white/15 transition-all hover:-translate-y-0.5 group"
                      data-testid={`kanban-card-${o.opportunity_id}`}>
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-3 h-3 text-slate-700 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold truncate">{o.title}</div>
                          <div className="text-[10px] text-slate-500 truncate">{o.contact_name || o.contact_email || "-"}</div>
                        </div>
                      </div>
                      {o.amount > 0 && <div className="text-xs font-bold mt-1.5" style={{ color: "#f7c935" }}>{o.amount.toLocaleString("fr")} EUR</div>}
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex items-center gap-1">
                          <div className="h-1 w-10 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${o.probability}%`, background: stage.color }} /></div>
                          <span className="text-[9px] text-slate-600">{o.probability}%</span>
                        </div>
                        {o.activities?.length > 0 && <span className="text-[9px] text-slate-600">{o.activities.length} act.</span>}
                      </div>
                      {o.ai_evaluation && (
                        <div className="mt-1.5 pt-1.5 border-t border-white/5">
                          <AIScoreBadge tier={o.ai_evaluation.tier} score={o.ai_evaluation.score} size="sm" testId={`kanban-ai-${o.opportunity_id}`} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {!loading && view === "list" && (
        <div>
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={filter.search} onChange={e => setFilter(f => ({ ...f, search: e.target.value }))} placeholder="Rechercher..." className="pl-9 bg-[#161020] border-white/10 text-white" />
            </div>
            <select value={filter.stage} onChange={e => setFilter(f => ({ ...f, stage: e.target.value }))} className="bg-[#161020] border border-white/10 text-white rounded-lg px-3 py-2 text-sm">
              <option value="">Toutes les etapes</option>
              {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div className="space-y-2" data-testid="opp-list">
            {filtered.map(o => {
              const st = STAGE_MAP[o.stage] || { color: "#64748b", label: o.stage };
              return (
                <div key={o.opportunity_id} onClick={() => setShowDetail(o)} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-[#f7c935]/20 cursor-pointer transition-colors flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold">{o.title}</div>
                    <div className="text-xs text-slate-500">{o.client_name || o.contact_email || "-"}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: st.color + "20", color: st.color }}>{st.label}</span>
                  <span className="text-sm font-bold">{o.amount?.toLocaleString("fr")} EUR</span>
                  <span className="text-xs text-slate-500">{o.probability}%</span>
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle opportunite</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Titre de l'opportunite" className={inputCls} data-testid="opp-title" />
            <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} className={`w-full ${inputCls} rounded-lg px-3 py-2 text-sm border border-white/10`} data-testid="opp-client">
              <option value="">Client existant (optionnel)</option>
              {clients.map(c => <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="Nom contact" className={inputCls} />
              <Input value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="Email contact" className={inputCls} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-slate-500 block mb-1">Montant EUR</label><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} className={inputCls} data-testid="opp-amount" /></div>
              <div><label className="text-xs text-slate-500 block mb-1">Probabilite %</label><Input type="number" value={form.probability} onChange={e => setForm(f => ({ ...f, probability: parseInt(e.target.value) || 0 }))} className={inputCls} /></div>
              <div><label className="text-xs text-slate-500 block mb-1">Date closing</label><Input type="date" value={form.expected_close} onChange={e => setForm(f => ({ ...f, expected_close: e.target.value }))} className={inputCls} /></div>
            </div>
            <Input value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} placeholder="Source" className={inputCls} />
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" rows={2} className={inputCls + " text-sm"} />
            <Button onClick={createOpp} disabled={saving || !form.title} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-create-opp">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Creer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {showDetail && (() => {
            const o = showDetail;
            const st = STAGE_MAP[o.stage] || { color: "#64748b", label: o.stage };
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 flex-wrap">
                    {o.title}
                    <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: st.color + "20", color: st.color }}>{st.label}</span>
                    {o.ai_evaluation && (
                      <AIScoreBadge tier={o.ai_evaluation.tier} score={o.ai_evaluation.score} size="sm" testId="dialog-ai-score" />
                    )}
                    <Link
                      to={`/admin/opportunite/${o.opportunity_id}`}
                      className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-[#f7c935]/15 text-[#f7c935] hover:bg-[#f7c935]/25 border border-[#f7c935]/30 font-semibold"
                      data-testid="open-full-detail-btn"
                    >
                      <ExternalLink className="w-3 h-3" /> Fiche complète
                    </Link>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Client:</span> {o.client_name || o.contact_name || "-"}</div>
                    <div><span className="text-slate-500">Contact:</span> {o.contact_email || "-"}</div>
                    <div><span className="text-slate-500">Montant:</span> <span className="font-bold text-[#f7c935]">{o.amount?.toLocaleString("fr")} EUR</span></div>
                    <div><span className="text-slate-500">Probabilite:</span> {o.probability}%</div>
                    <div><span className="text-slate-500">Source:</span> {o.source || "-"}</div>
                    <div><span className="text-slate-500">Closing:</span> {o.expected_close || "-"}</div>
                  </div>

                  {/* Stage buttons */}
                  <div>
                    <label className="text-xs text-slate-500 block mb-2">Changer l'etape</label>
                    <div className="flex flex-wrap gap-1.5">
                      {stages.map(s => (
                        <button key={s.key} onClick={() => updateStage(o.opportunity_id, s.key)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${o.stage === s.key ? "ring-2 ring-white/30" : "hover:brightness-125"}`}
                          style={{ background: s.color + "20", color: s.color }}
                          data-testid={`stage-btn-${s.key}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {o.notes && <div className="text-sm text-slate-400 bg-[#161020] rounded-lg p-3">{o.notes}</div>}

                  {/* Timeline */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-400 mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Timeline</h3>
                    <div className="flex gap-2 mb-3">
                      <select value={activityType} onChange={e => setActivityType(e.target.value)} className="bg-[#161020] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs w-24" data-testid="crm-activity-type">
                        <option value="note">Note</option><option value="call">Appel</option><option value="email">Email</option><option value="sms">SMS</option>
                      </select>
                      <Input value={activityText} onChange={e => setActivityText(e.target.value)} onKeyDown={e => e.key === "Enter" && addActivity()} placeholder="Ajouter..." className={inputCls + " text-sm flex-1"} data-testid="activity-input" />
                      <Button onClick={addActivity} disabled={!activityText.trim()} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825]" data-testid="add-activity-btn"><Send className="w-4 h-4" /></Button>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(o.activities || []).slice().reverse().map((a, idx) => {
                        const tc = { note: { icon: MessageSquare, color: "#3b82f6", label: "Note" }, call: { icon: PhoneCall, color: "#22c55e", label: "Appel" }, email: { icon: Mail, color: "#8b5cf6", label: "Email" }, sms: { icon: MessagesSquare, color: "#06b6d4", label: "SMS" }, conversion: { icon: CheckCircle2, color: "#22c55e", label: "Conversion" } }[a.type] || { icon: MessageSquare, color: "#3b82f6", label: a.type };
                        const Ic = tc.icon;
                        return (
                          <div key={a.activity_id || idx} className="bg-[#161020] rounded-lg p-2.5 text-sm">
                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: tc.color + "20" }}><Ic className="w-3 h-3" style={{ color: tc.color }} /></div>
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: tc.color + "15", color: tc.color }}>{tc.label}</span>
                              {a.source === "prospect" && <span className="text-[9px] text-slate-600 bg-white/5 px-1 rounded">Prospect</span>}
                              <span className="font-medium" style={{ color: "#f7c935" }}>{a.by_name || a.author_name || "Admin"}</span>
                              <span>{new Date(a.at || a.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                            </div>
                            <p className="text-slate-300 ml-7">{a.content}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {o.stage !== "gagne" && o.stage !== "perdu" && (
                      <Button onClick={() => { setConvertForm({ type: "devis", lines: [{ description: o.title, quantity: 1, unit_price: o.amount, tva_rate: 20 }] }); setShowConvert(o.opportunity_id); }}
                        className="bg-[#3b82f6] text-white hover:bg-[#2563eb] text-xs" data-testid="convert-opp-btn">
                        <FileText className="w-4 h-4 mr-1" /> Convertir en devis
                      </Button>
                    )}
                    {o.devis_id && <span className="text-xs text-blue-400 flex items-center gap-1"><FileText className="w-3 h-3" /> Devis lie</span>}
                    <Button onClick={() => deleteOpp(o.opportunity_id)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs ml-auto" data-testid="delete-opp-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Convert to Devis/Facture - Fullscreen InvoiceEditor */}
      {showDevisEditor && (() => {
        const opp = opps.find(o => o.opportunity_id === showConvert);
        const saveDevis = async () => {
          setSaving(true);
          try {
            const payload = {
              ...devisForm,
              type: devisForm.type || "devis",
              opportunity_id: showConvert,
            };
            await api.post("/facturation/documents", payload);
            setShowDevisEditor(false);
            setShowConvert(null);
            reload();
          } catch (e) { console.error(e); }
          setSaving(false);
        };
        return (
          <div className="fixed inset-0 z-50 bg-[#0b0811] flex flex-col" data-testid="devis-editor-fullscreen">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10 bg-[#14101c] flex-shrink-0">
              <button onClick={() => { setShowDevisEditor(false); setShowConvert(null); }} className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm" data-testid="devis-editor-back">
                <X className="w-4 h-4" /> Fermer
              </button>
              <div className="w-px h-5 bg-white/10" />
              <span className="text-sm font-bold text-white">Creer {devisForm.type === "facture" ? "une facture" : "un devis"}</span>
              {opp && <span className="text-xs text-slate-500">pour {opp.contact_name || opp.title}</span>}
              <div className="flex-1" />
              <select value={devisForm.type || "devis"} onChange={e => setDevisForm(f => ({ ...f, type: e.target.value }))}
                className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-2.5 py-1 text-xs mr-2" data-testid="devis-type-select">
                <option value="devis">Devis</option>
                <option value="facture">Facture</option>
              </select>
              <Button onClick={saveDevis} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-8 text-xs px-4" data-testid="devis-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <ArrowRight className="w-3.5 h-3.5 mr-1.5" />}
                Generer
              </Button>
            </div>
            <div className="flex-1 overflow-hidden">
              <InvoiceEditor form={devisForm} setForm={setDevisForm} tags={tags} />
            </div>
          </div>
        );
      })()}

      {/* Convert to Devis Dialog - Simple fallback */}
      <Dialog open={!!showConvert && !showDevisEditor} onOpenChange={() => setShowConvert(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#f7c935]" /> Creer un document</DialogTitle></DialogHeader>
          {showConvert && (() => {
            const opp = opps.find(o => o.opportunity_id === showConvert);
            if (!opp) return null;
            return (
              <div className="space-y-4 mt-4">
                <p className="text-sm text-[#888194]">Opportunite: <span className="text-white font-medium">{opp.title}</span></p>
                <p className="text-sm text-[#888194]">Montant: <span className="text-[#f7c935] font-bold">{opp.amount?.toLocaleString("fr")} EUR</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => {
                    setDevisForm({
                      name: `Devis - ${opp.title}`,
                      type: "devis",
                      primary_color: "#f7c935",
                      header_text: `Devis pour ${opp.contact_name || opp.title}`,
                      items: [{ description: opp.title || "Prestation", qty: 1, unit_price: opp.amount || 0, tva: 20 }],
                      footer_text: "",
                      cgv: "",
                    });
                    setShowDevisEditor(true);
                  }} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="open-devis-editor">
                    <FileText className="w-4 h-4 mr-2" /> Devis
                  </Button>
                  <Button onClick={() => {
                    setDevisForm({
                      name: `Facture - ${opp.title}`,
                      type: "facture",
                      primary_color: "#f7c935",
                      header_text: `Facture pour ${opp.contact_name || opp.title}`,
                      items: [{ description: opp.title || "Prestation", qty: 1, unit_price: opp.amount || 0, tva: 20 }],
                      footer_text: "",
                      cgv: "",
                    });
                    setShowDevisEditor(true);
                  }} variant="outline" className="border-white/10 text-white hover:bg-white/5 font-bold" data-testid="open-facture-editor">
                    <FileText className="w-4 h-4 mr-2" /> Facture
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Pipeline Config Dialog */}
      <Dialog open={showPipelineConfig} onOpenChange={setShowPipelineConfig}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5" /> Configurer le pipeline</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400 mt-2">Ajoutez, modifiez ou reordonnez les etapes de votre pipeline commercial.</p>
          <div className="space-y-2 mt-4 max-h-[50vh] overflow-y-auto">
            {editStages.map((s, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#0b0811] rounded-lg p-2.5" data-testid={`config-stage-${i}`}>
                <GripVertical className="w-4 h-4 text-slate-600 flex-shrink-0" />
                <input type="color" value={s.color || "#64748b"} onChange={e => { const u = [...editStages]; u[i].color = e.target.value; setEditStages(u); }} className="w-7 h-7 rounded border-none cursor-pointer" />
                <Input value={s.label} onChange={e => { const u = [...editStages]; u[i].label = e.target.value; setEditStages(u); }}
                  className="bg-[#14101c] border-white/10 text-white text-xs h-8 flex-1" placeholder="Nom de l'etape" />
                <Input value={s.key} onChange={e => { const u = [...editStages]; u[i].key = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""); setEditStages(u); }}
                  className="bg-[#14101c] border-white/10 text-slate-500 text-[10px] h-8 w-28" placeholder="cle_unique" />
                <button onClick={() => setEditStages(editStages.filter((_, idx) => idx !== i))} className="p-1 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
            ))}
          </div>
          <Button onClick={() => setEditStages([...editStages, { key: `stage_${Date.now()}`, label: "Nouvelle etape", color: "#64748b", order: editStages.length }])}
            variant="outline" size="sm" className="border-white/10 text-white hover:bg-white/5 text-xs mt-2 w-full"><Plus className="w-3 h-3 mr-1" /> Ajouter une etape</Button>
          <Button onClick={savePipelineConfig} disabled={saving} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold mt-3" data-testid="save-pipeline-config">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </DialogContent>
      </Dialog>

      {/* Agent IA CRM */}
      {showAgent && (
        <div className="fixed bottom-6 right-6 w-[420px] z-50 shadow-2xl shadow-black/50" data-testid="crm-agent-panel">
          <AgentChat agentType="crm" agentName="Agent CRM" agentColor="#3b82f6" onClose={() => setShowAgent(false)} />
        </div>
      )}

      {/* Routines IA CRM - Full width integrated panel */}
      {showRoutines && (
        <div className="mt-4 mb-2" data-testid="crm-routines-section">
          <CrmRoutinesPanel onClose={() => setShowRoutines(false)} />
        </div>
      )}
    </div>
  );
}
